import { Component, forceUpdate, h, Host, Prop, State } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";

@Component({
    tag: "ux-chat-item",
    styleUrl: "chat-item.scss",
    shadow: true
})
export class ChatItem {
    /**
     * The name of the user who sent the chat message.
     * @required
     */
    @Prop() readonly name!: string;

    /**
     * The date when the chat message was sent.
     */
    @Prop() readonly date!: string;

    /**
     * Indicates whether the chat message was sent by the current user system or bot
     */
    @Prop({ reflect: true }) readonly source!: "user" | "system" | "bot";

    /**
     * Optional URL for the profile picture. If not provided, the default icon will be shown for bot messages.
     */
    @Prop() readonly profilePicture?: string;

    /**
     * State to track the current locale
     */
    @State() locale: string;

    componentWillLoad() {
        // Subscribe to i18nProvider updates and update locale
        this.updateLocale();
        i18nProvider.subscribe(() => {
            this.updateLocale();
            forceUpdate(this);
        });
    }

    private updateLocale() {
        const { language } = i18nProvider;

        const defaultRegionMap: Record<string, string> = {
            en: "en-US",
            de: "de-DE"
        };

        this.locale = defaultRegionMap[language] || `${language}-${language.toUpperCase()}`;
    }

    /**
     * Formats the date using Intl.DateTimeFormat
     */
    private formatDate(dateString: string): string {
        try {
            const date = new Date(dateString);

            const formatter = new Intl.DateTimeFormat(this.locale, {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
            });
            return formatter.format(date);
        } catch {
            return "Invalid date";
        }
    }

    render() {
        return (
            <Host role="listitem">
                <div id="chat-item-container">
                    {this.source === "bot" && (
                        <div class="profile-picture">
                            {this.profilePicture ? (
                                <img src={this.profilePicture} alt={`${this.name} profile picture`} />
                            ) : (
                                <ux-icon name="ai-assistent" size-s="35" weight="normal" fill="white"></ux-icon>
                            )}
                        </div>
                    )}
                    <div>
                        <div class="message-content">
                            <slot></slot>
                        </div>
                        {(this.name || this.date) && (
                            <div class={`message-meta ${this.source === "user" ? "user-message-footer" : ""}`}>
                                <ux-copy class="chat-name" variant="tiny">
                                    {[this.name, this.date && this.formatDate(this.date)].filter(Boolean).join(" - ")}
                                </ux-copy>
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
